import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { LayoutDashboard, Car, CalendarCheck, Users, ExternalLink, ShieldAlert, IdCard, MapPin, Zap, Inbox, Star, Settings, TicketPercent, BarChart3, LifeBuoy, ScrollText, Plug, Menu, X } from "lucide-react";
import { useMe } from "@/lib/api";
import { Logo } from "@/components/site/SiteLayout";

const groups = [
  { title: "Overview", items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "admin-nav-dashboard" }] },
  { title: "Operations", items: [
    { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, id: "admin-nav-bookings" },
    { to: "/admin/cars", label: "Vehicles", icon: Car, id: "admin-nav-cars" },
    { to: "/admin/fleet", label: "Fleet tracking", icon: MapPin, id: "admin-nav-fleet" },
    { to: "/admin/verifications", label: "KYC & Verification", icon: IdCard, id: "admin-nav-verifications" },
    { to: "/admin/customers", label: "Customers", icon: Users, id: "admin-nav-customers" },
  ] },
  { title: "Finance & growth", items: [
    { to: "/admin/reports", label: "Reports & GST", icon: BarChart3, id: "admin-nav-reports" },
    { to: "/admin/pricing", label: "Pricing", icon: Zap, id: "admin-nav-pricing" },
    { to: "/admin/coupons", label: "Offers & Coupons", icon: TicketPercent, id: "admin-nav-coupons" },
    { to: "/admin/reviews", label: "Reviews", icon: Star, id: "admin-nav-reviews" },
  ] },
  { title: "Support", items: [
    { to: "/admin/tickets", label: "Support tickets", icon: LifeBuoy, id: "admin-nav-tickets" },
    { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, id: "admin-nav-enquiries" },
  ] },
  { title: "System", items: [
    { to: "/admin/integrations", label: "Integrations", icon: Plug, id: "admin-nav-integrations" },
    { to: "/admin/audit", label: "Audit logs", icon: ScrollText, id: "admin-nav-audit" },
    { to: "/admin/settings", label: "Settings", icon: Settings, id: "admin-nav-settings" },
  ] },
];

function Sidebar({ close }) {
  return (
    <div className="admin-sidebar flex h-full flex-col bg-hc-deep p-5">
      <div className="flex items-center justify-between"><Logo dark />{close && <button onClick={close} aria-label="Close menu" className="text-white"><X className="h-5 w-5" /></button>}</div>
      <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-hc-gold/80">Operations panel</p>
      <nav className="mt-6 flex-1 space-y-5 overflow-y-auto pr-1">
        {groups.map((g) => (
          <div key={g.title}>
            <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">{g.title}</p>
            {g.items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.id} onClick={close}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? "!bg-hc-gold !text-hc-ink shadow" : "text-white/75 hover:bg-white/10 hover:text-white"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <Link to="/" className="mt-4 flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" />View site</Link>
    </div>
  );
}

function Gate() {
  const { me, loading } = useMe();
  const [open, setOpen] = useState(false);
  if (loading) return <div className="p-10 text-hc-slate">Loading…</div>;
  if (me?.role !== "admin")
    return (
      <div className="grid min-h-screen place-items-center p-6" data-testid="admin-denied">
        <div className="card-hc max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-hc-gold" />
          <h1 className="mt-4 text-2xl font-semibold">Admin access only</h1>
          <p className="mt-2 text-sm text-hc-slate">Your account does not have admin rights.</p>
          <Link to="/" className="btn-primary mt-6">Back to site</Link>
        </div>
      </div>
    );
  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 md:block"><Sidebar /></aside>
      {open && <div className="fixed inset-0 z-50 bg-black/40 md:hidden" onClick={() => setOpen(false)}><div className="h-full w-72" onClick={(e) => e.stopPropagation()}><Sidebar close={() => setOpen(false)} /></div></div>}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-hc-sage bg-white/90 px-5 backdrop-blur">
          <button className="md:hidden" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-menu-toggle"><Menu className="h-6 w-6 text-hc-ink" /></button>
          <p className="hidden text-sm text-hc-slate md:block">Signed in as <span className="font-semibold text-hc-ink" data-testid="admin-email">{me.email}</span></p>
          <UserButton />
        </header>
        <main className="flex-1 p-5 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const loc = useLocation();
  useEffect(() => {
    document.documentElement.classList.add("admin-light");
    return () => document.documentElement.classList.remove("admin-light");
  }, []);
  const back = encodeURIComponent(window.location.origin + loc.pathname);
  return (
    <div className="min-h-screen bg-hc-cream text-hc-ink" data-testid="admin-layout">
      <Show when="signed-in"><Gate /></Show>
      <Show when="signed-out"><Navigate to={`/admin/sign-in?redirect_url=${back}`} replace /></Show>
    </div>
  );
}
